import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formattedDateTimeForLocale } from 'helpers/Datetime.helper';
import { getLanguageValue } from 'locales/i18n';
import { PAGE_TYPE } from 'enums';
import { INotice } from 'api/interfaces/bbs.interface';
import { LoadingComponent } from 'commons/loading';
import NoticeDetailSkeleton from './NoticeDetailSkeleton';
import NoticeImage from './NoticeImage';
import { getCurrentLanguageCode } from "helpers/String.helper";
interface NoticeDetailProps {
    notice: INotice | null;
    isLoading: boolean;
}

const NoticeDetailComponent: React.FC<NoticeDetailProps> = ({ notice, isLoading }) => {
    const navigate = useNavigate();
    
    if (isLoading) {
        return <NoticeDetailSkeleton />;
    }

    if (!notice) {
        return (
            <main className="bg-gray-50 min-h-screen py-6">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                            <div className="p-6 text-center">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtNoticeNotFound")}
                                </h3>
                                <p className="text-gray-500 mb-6">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtNoticeNotFoundDesc")}
                                </p>
                                <Link
                                    to="/student/my/notice"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#CF3688] hover:bg-[#B8327A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#CF3688]"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtBackToList")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        );
    }
    const currentLangCode = getCurrentLanguageCode();
    const currentContent = notice.contents?.find(
        content => content.languageCode === currentLangCode && content.isUsing
    ) || notice.contents?.find(
        content => content.languageCode === currentLangCode
    ) || notice.contents?.find(
        content => content.languageCode === 'en' && content.isUsing
    ) || notice.contents?.find(
        content => content.languageCode === 'en'
    ) || notice.contents?.[0];

    if (!currentContent) {
        return (
            <main className="bg-gray-50 min-h-screen py-6">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                            <div className="p-6 text-center">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtContentNotFound")}
                                </h3>
                                <p className="text-gray-500 mb-6">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtContentNotFoundDesc")}
                                </p>
                                <Link
                                    to="/student/my/notice"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#CF3688] hover:bg-[#B8327A] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#CF3688]"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtBackToList")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="bg-gray-50 min-h-screen py-6">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    {/* 헤더 영역 */}
                    <div className="mb-6">
                        <div className="flex items-center gap-4 mb-4">
                            <button onClick={() => navigate('/student/my/notice')} className="text-gray-600 hover:text-gray-900">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            {notice.isImportant && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtImportant")}
                                </span>
                            )}
                            <h1 className="text-2xl font-bold text-gray-900 flex-1">
                                {currentContent.title}
                            </h1>
                        </div>

                        {/* 작성일시 */}
                        <div className="text-sm text-gray-500 ml-10">
                            <span>{formattedDateTimeForLocale(notice.createdAt)}</span>
                        </div>
                    </div>

                    {/* 공지사항 상세 */}
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">

                        {/* 내용 */}
                        <div className="px-6 py-8">
                            <div className="prose prose-gray max-w-none">
                                <div
                                    className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                                    dangerouslySetInnerHTML={{ __html: currentContent.content }}
                                />

                                {/* 반응형 첨부 이미지 */}
                                <NoticeImage content={currentContent} />
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </main>
    );
};

export default NoticeDetailComponent;
